import { gql } from "@apollo/client";
import { FaustTemplate } from "@faustwp/core";
import { SEO } from "@components";
import { GetPressReleasePageDataQuery } from "@graphqlTypes";
import FlexibleContentMap from "@/components/ComponentMaps/FlexibleContentMap";
import { FlexiblePageContentFragment } from "@/fragments/FlexibleContent";
import { usePageHighlight } from "@/providers/PageHighlightContext";
import { HIGHLIGHT_COLOR_BY_TYPE, HIGHLIGHT_COLORS } from "@/core/constants";
import { useEffect } from "react";
import { getBasicQueryVars, getPageTitle } from "@/core/utils";
import { PageDataFragment } from "@/fragments/PageData";
import { usePageLoading } from "@/providers/LoadingContext";
import { useTrackRouteChange } from "@/providers/TrackingContext";
import { usePageWrapperData } from "@/providers/PageWrapperContext";

const PressReleasesPage: FaustTemplate<GetPressReleasePageDataQuery> = ({ pageWrapperData, data, loading }: FaustPageProps<GetPressReleasePageDataQuery>) => {
  const { siteTitle } = usePageWrapperData();
  const { recentStories } = pageWrapperData;
  const { setHighlightColor } = usePageHighlight();
  const { initialPressReleases } = data;
  const { title, flexiblePageContent, pageData: { pageCategory, ogTitle, ogDescription, featuredImage } = {} } = data?.rhPage || {};
  const seoTitle = getPageTitle(siteTitle, ogTitle || title);
  const { previewLoading, setPreviewLoading } = usePageLoading();
  const [category] = pageCategory || [];

  useEffect(() => {
    setHighlightColor(HIGHLIGHT_COLOR_BY_TYPE[category?.slug] || HIGHLIGHT_COLORS.GREEN);
  }, [category]);
  useTrackRouteChange(category?.slug);

  useEffect(() => {
    if (!loading && previewLoading) setPreviewLoading(false);
  }, [loading, previewLoading]);

  if (loading) return;

  return (
    <>
      <SEO
        title={seoTitle}
        description={ogDescription || ""}
        image={featuredImage}
      />
      <FlexibleContentMap
        content={flexiblePageContent?.content}
        initialPressReleases={initialPressReleases}
        recentStories={recentStories}
      />
    </>
  );
};

PressReleasesPage.variables = getBasicQueryVars;

PressReleasesPage.query = gql`
  ${PageDataFragment}
  ${FlexiblePageContentFragment}
  query GetPressReleasePageData($databaseId: ID!, $asPreview: Boolean = false, $language: String = "en") {
    rhPage(id: $databaseId, asPreview: $asPreview, language: $language) {
      title
      ...PageData
      flexiblePageContent {
        ...ContentFragment
      }
    }
    initialPressReleases: pressReleases(
      where: { language: $language, offsetPagination: { size: 12 }, sortByDate: true }
    ) {
      pageInfo {
        offsetPagination {
          hasMore
          total
        }
      }
      nodes {
        title
        uri
        pressReleaseFields {
          publishDate
        }
      }
    }
  }
`;

export default PressReleasesPage;
